import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Translate, I18n } from 'react-redux-i18n';
import alertify from 'alertifyjs';
import * as commentActions from 'redux/actions/commentActions';
import StockAvatar from 'components/StockAvatar';
import { layoutTreemap, heatColor } from './treemapLayout';
import {
    buildShareHeatmapSvg,
    svgStringToPngBlob,
    loadHeatmapAvatars,
    HEATMAP_EXPORT_SIZE,
} from './heatmapExport';

const VIEW_W = 900;
const VIEW_H = 520;
const PAD = 1.5;

const HeatmapModal = ({ open, list, onClose, user_id, shareToWall }) => {
    const [sharing, setSharing] = useState(false);

    const cells = useMemo(() => {
        const rows = (Array.isArray(list) ? list : [])
            .map((item) => {
                const pl = Number(item.pl) || 0;
                return {
                    id: item.symbol,
                    symbol: item.symbol,
                    pl,
                    roi: Number(item.roi) || 0,
                    count: Number(item.count) || 0,
                    value: Math.abs(pl),
                    item,
                };
            })
            .filter((item) => item.value > 0);

        const totalGains = rows.reduce(
            (sum, row) => sum + (row.pl > 0 ? row.pl : 0),
            0,
        );
        const totalLosses = rows.reduce(
            (sum, row) => sum + (row.pl < 0 ? Math.abs(row.pl) : 0),
            0,
        );
        const maxAbs = Math.max(...rows.map((row) => row.value), 1);

        return layoutTreemap(rows, VIEW_W, VIEW_H).map((cell) => {
            const shareBase = cell.pl >= 0 ? totalGains : totalLosses;
            const sharePct = shareBase > 0
                ? (Math.abs(cell.pl) / shareBase) * 100
                : 0;
            return {
                ...cell,
                color: heatColor(cell.pl, maxAbs),
                sharePct,
            };
        });
    }, [list]);

    if (!open) {
        return null;
    }

    const handleBackdropClick = (event) => {
        if (event.target === event.currentTarget) {
            onClose();
        }
    };

    const handleShare = async () => {
        if (!user_id || !cells.length || sharing) {
            return;
        }

        setSharing(true);
        try {
            const avatars = await loadHeatmapAvatars(cells);
            const svg = buildShareHeatmapSvg(cells, {
                title: I18n.t('report_heatmap'),
                subtitle: I18n.t('report_heatmap_share_subtitle'),
                width: HEATMAP_EXPORT_SIZE.width,
                height: HEATMAP_EXPORT_SIZE.height,
                avatars,
            });
            const pngBlob = await svgStringToPngBlob(
                svg,
                HEATMAP_EXPORT_SIZE.width,
                HEATMAP_EXPORT_SIZE.height,
            );
            const file = new File([pngBlob], `heatmap-${Date.now()}.png`, {
                type: 'image/png',
            });
            const previewMedia = URL.createObjectURL(pngBlob);
            const params = new FormData();
            params.append('comment', I18n.t('report_heatmap_share_comment'));
            params.append('id', user_id);
            params.append('file', file);

            await shareToWall({ params, previewMedia });
            alertify.success(I18n.t('report_heatmap_share_success'));
            onClose();
        } catch (error) {
            alertify.error(error?.message || I18n.t('try_later'));
        } finally {
            setSharing(false);
        }
    };

    return (
        <div
            className="report-heatmap-overlay"
            onClick={handleBackdropClick}
            role="presentation"
        >
            <div
                className="report-heatmap-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="report-heatmap-title"
            >
                <div className="report-heatmap-header">
                    <div className="report-heatmap-header-text">
                        <h4 id="report-heatmap-title">
                            <Translate value="report_heatmap" />
                        </h4>
                        <p className="report-heatmap-subtitle">
                            <Translate value="report_heatmap_hint" />
                        </p>
                    </div>
                    <div className="report-heatmap-header-actions">
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm report-heatmap-share"
                            onClick={handleShare}
                            disabled={sharing || !cells.length || !user_id}
                        >
                            {sharing
                                ? <Translate value="report_heatmap_sharing" />
                                : <Translate value="report_heatmap_share" />}
                        </button>
                        <button
                            type="button"
                            className="btn btn-text btn-sm report-heatmap-close"
                            onClick={onClose}
                        >
                            <Translate value="close" />
                        </button>
                    </div>
                </div>

                <div className="report-heatmap-body">
                    {cells.length === 0 ? (
                        <p className="report-heatmap-empty">
                            <Translate value="report_heatmap_empty" />
                        </p>
                    ) : (
                        <svg
                            className="report-heatmap-svg"
                            viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
                            role="img"
                            aria-label="Heatmap"
                        >
                            {cells.map((cell) => {
                                const showPct = cell.width > 78 && cell.height > 56;
                                const showDetail = cell.width > 70 && cell.height > 42;
                                const showSymbol = cell.width > 36 && cell.height > 24;
                                const x = cell.x + PAD;
                                const y = cell.y + PAD;
                                const w = Math.max(0, cell.width - PAD * 2);
                                const h = Math.max(0, cell.height - PAD * 2);
                                const plText = `${cell.pl >= 0 ? '+' : ''}${cell.pl.toFixed(0)}`;
                                const pctText = `${cell.sharePct.toFixed(1)}%`;
                                const shareLabel = cell.pl >= 0
                                    ? I18n.t('report_heatmap_of_gains')
                                    : I18n.t('report_heatmap_of_losses');

                                const minSide = Math.min(w, h);
                                const showAvatar = minSide >= 70;
                                const avatarSize = showAvatar
                                    ? Math.max(24, Math.min(72, Math.floor(minSide * 0.36)))
                                    : 0;
                                const gap = showAvatar ? 6 : 0;
                                const lineH = 14;
                                const textLines = (showSymbol ? 1 : 0)
                                    + (showDetail ? 1 : 0)
                                    + (showPct ? 1 : 0);
                                const textBlockH = textLines * lineH;
                                const contentH = avatarSize
                                    + (showAvatar && textLines ? gap : 0)
                                    + textBlockH;
                                const contentTop = y + Math.max(4, (h - contentH) / 2);
                                const avatarX = x + (w - avatarSize) / 2;
                                const textStartY = contentTop
                                    + avatarSize
                                    + (showAvatar ? gap : 0)
                                    + 11;
                                let textLine = 0;
                                const nextTextY = () => {
                                    const ty = textStartY + textLine * lineH;
                                    textLine += 1;
                                    return ty;
                                };

                                return (
                                    <g key={cell.symbol}>
                                        <title>
                                            {`${cell.symbol}: $${cell.pl.toFixed(2)} (${pctText} ${shareLabel}) · ROI ${cell.roi.toFixed(2)}% · ${cell.count} trades`}
                                        </title>
                                        <rect
                                            x={x}
                                            y={y}
                                            width={w}
                                            height={h}
                                            rx={4}
                                            ry={4}
                                            fill={cell.color}
                                        />
                                        {showAvatar && (
                                            <foreignObject
                                                x={avatarX}
                                                y={contentTop}
                                                width={avatarSize}
                                                height={avatarSize}
                                            >
                                                <div
                                                    xmlns="http://www.w3.org/1999/xhtml"
                                                    className="portfolio-heatmap-avatar-wrap"
                                                    style={{ width: avatarSize, height: avatarSize }}
                                                >
                                                    <StockAvatar
                                                        symbol={cell.symbol}
                                                        item={cell.item}
                                                        className="portfolio-heatmap-avatar"
                                                    />
                                                </div>
                                            </foreignObject>
                                        )}
                                        {showSymbol && (
                                            <text
                                                x={x + w / 2}
                                                y={nextTextY()}
                                                textAnchor="middle"
                                                className="report-heatmap-symbol"
                                            >
                                                {cell.symbol}
                                            </text>
                                        )}
                                        {showDetail && (
                                            <text
                                                x={x + w / 2}
                                                y={nextTextY()}
                                                textAnchor="middle"
                                                className="report-heatmap-pl"
                                            >
                                                {`$${plText}`}
                                            </text>
                                        )}
                                        {showPct && (
                                            <text
                                                x={x + w / 2}
                                                y={nextTextY()}
                                                textAnchor="middle"
                                                className="report-heatmap-pct"
                                            >
                                                {pctText}
                                            </text>
                                        )}
                                    </g>
                                );
                            })}
                        </svg>
                    )}
                </div>
            </div>
        </div>
    );
};

HeatmapModal.propTypes = {
    open: PropTypes.bool,
    list: PropTypes.array,
    onClose: PropTypes.func.isRequired,
    user_id: PropTypes.string,
    shareToWall: PropTypes.func.isRequired,
};

HeatmapModal.defaultProps = {
    open: false,
    list: [],
    user_id: '',
};

function mapStateToProps(reducer) {
    return {
        user_id: reducer.session.auth.id,
    };
}

const mapDispatchToProps = (dispatch) => ({
    shareToWall: (data) => dispatch(commentActions.newItem(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(HeatmapModal);
